import { Box, useTheme } from '@mui/material';
import Parcoords from 'parcoord-es';
import 'parcoord-es/dist/parcoords.css';
import i18n from 'i18n';
import { useEffect, useMemo, useRef, useState } from 'react';

let chartView = null
interface Dimension {
  type: string;
  title: string;
  order: number;
}


const Chart = props => {
  const { data, colorSet, mouseOverRow, updateBrushedData, brushedData, dimensions,  isDimensionsHidden, reloadChart } = props
  const chartRef = useRef(null);
  const chartWidthRef = useRef('100%');
  const [chartData, setChartData] = useState([...data])
  const theme = useTheme();
  const [chartHeight, setChartHeight] = useState(500);

  const handleReset = () => {
    reloadChart(); 
  };


  const processBrushedData = useMemo(() => {
    let brushed = {}
    brushedData.map(brush => {
      if (brush.selection.scaled.length !== 0) {
        if (brush.selection.scaled.length === 1)
          brush.selection.scaled.push(brush.selection.scaled[0])
        brushed[brush.axis] = brush.selection.scaled.sort((a, b) => a - b)
      }
      return true
    })
    return brushed
  }, [brushedData])

  const handleBrush = (d, e) => {
    updateBrushedData(d, e)
  }

  const handleTextLabels = () => {
    let texts = document.getElementsByClassName('label')
    for (let i = 0; i < texts.length; i++) {
      const element = texts[i] as SVGTextElement;
      element.style.pointerEvents = 'none';
      let fullText = element.getAttribute('label') || element.innerHTML;
      
      const shortLabels = ["id", "rating"];
      if (shortLabels.includes(fullText) || fullText.length <= 6) {
        element.setAttribute('text-anchor', 'middle');
        element.setAttribute('dy', '-1em');
        element.textContent = fullText;
      } else {
        element.setAttribute('transform', 'rotate(-45)');
        element.setAttribute('text-anchor', 'start');
        
        if (fullText.length > 70) {
          element.textContent = '';
          
          //const midpoint = Math.ceil(fullText.length / 3);
          const part1 = fullText.substring(0, 24);
          const part2 = fullText.substring(24, 48);
          const part3 = fullText.substring(48);
          
          const tspan1 = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
          tspan1.textContent = part1;
          tspan1.setAttribute('x', '0.3em');
          tspan1.setAttribute('dy', '-3.5em');
          element.appendChild(tspan1);
          
          const tspan2 = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
          tspan2.textContent = part2;
          tspan2.setAttribute('x', '0');
          tspan2.setAttribute('dy', '1.2em');
          element.appendChild(tspan2);

          const tspan3 = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
          tspan3.textContent = part3;
          tspan3.setAttribute('x', '0');
          tspan3.setAttribute('dy', '1.2em');
          element.appendChild(tspan3);

          
        } 
        else if (fullText.length > 30) {
           element.textContent = '';
          
          //const midpoint = Math.ceil(fullText.length / 2);
          const part1 = fullText.substring(0, 26);
          const part2 = fullText.substring(26);
          
          const tspan1 = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
          tspan1.textContent = part1;
          tspan1.setAttribute('x', '0.3em');
          tspan1.setAttribute('dy', '-2.5em');
          element.appendChild(tspan1);
          
          const tspan2 = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
          tspan2.textContent = part2;
          tspan2.setAttribute('x', '0');
          tspan2.setAttribute('dy', '1.2em');
          element.appendChild(tspan2);
        }
        else {
          element.setAttribute('dy', '-1.8em');
          element.setAttribute('dx', '-0.5em');
          element.textContent = fullText;
        }
      }
    }
  }


  const handleResize = () => {
    if (chartView && chartRef.current) {
      chartWidthRef.current = '100%';
      requestAnimationFrame(() => {
        chartView
          .width(chartRef.current.clientWidth)
          .updateAxes();
      });
    }
    handleReset()
  };


  useEffect(() => {
    setChartData(data)
    handleReset()
  }, [data,isDimensionsHidden])


  useEffect(() => {
   
    handleResize();
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleReset, 150);
    };
    
    window.addEventListener('resize', debouncedResize);
    
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  useEffect(() => {
    if (chartRef !== null && chartData !== null) {
      chartRef.current.innerHTML = ''
      const brushed = processBrushedData
      // V4 shell forces CSS direction:ltr — still reverse axes for Hebrew like production.
      const isRTL =
        theme.direction === 'rtl' || (i18n.language || '').startsWith('he');
      const orderedDimensions = isRTL
        ? Object.keys(dimensions).reverse().reduce((obj, key) => {
          obj[key] = dimensions[key];
          return obj;
        }, {})
        : dimensions;

      // order chart data by idx number 
      const sortedChartData = [...chartData].sort((a, b) => {
        const idxA = typeof a.idx === 'string' ? parseInt(a.idx, 10) : a.idx;
        const idxB = typeof b.idx === 'string' ? parseInt(b.idx, 10) : b.idx;
        return idxA - idxB;
      });

 
      chartView = Parcoords()(chartRef.current)
        .data(sortedChartData)  
        .color((d, i) => {
          try {
            return colorSet[d.rating].color
          } catch (error) {
            return 'grey'
          }
        })
        .alpha(0.4)
        .mode("queue")
        .margin({
          top: 140,
          // `#` / idx ticks sit outside the first/last axis — a few more px
          // so they are not clipped (LTR: left; RTL: right after axis reverse).
          left: isRTL ? 15 : 14,
          right: isRTL ? 14 : 15,
          bottom: 16
        })
        .dimensions(orderedDimensions)
        .hideAxis(["id", ...isDimensionsHidden])
        .render()
        .reorderable()
        .createAxes()
        .brushMode("1D-axes")
        .brushExtents(brushed)
        .on("brush", handleBrush)

      handleTextLabels();
    }
  }, [chartRef, data, chartData, colorSet, processBrushedData, dimensions, isDimensionsHidden, theme.direction, i18n.language])

  
  useEffect(() => {
    if (mouseOverRow !== null) {
      const item_id = mouseOverRow.id
      const d = chartData;
      const elementPos = d.map(function (x) { return x.id; }).indexOf(item_id);
      if (elementPos > -1) chartView.highlight([d[elementPos]]);
    } else {
      colorSet !== null && chartView.unhighlight();
    }
  }, [mouseOverRow])


  return (
    <Box dir='ltr' sx={{ width: '100%', overflowX: 'auto' }}>
      <div 
        ref={chartRef} 
        id={'chart-id'} 
        style={{ 
          height: chartHeight, 
          width: chartWidthRef.current
        }} 
        className={'parcoords'} 
      />
    </Box>
  );
}

export default Chart;